import type { SaveData } from '../types';

// 5 named slots. Each slot stores its own character/animal/level. The
// "active" slot is whichever one the player most recently chose to play.
// Old single-slot saves (v1) are migrated into slot 0 on first read.

const SLOTS_KEY = 'animal-quest:saves:v2';
const ACTIVE_KEY = 'animal-quest:active-slot:v2';
const LEGACY_KEY = 'animal-quest:save:v1';

export const SLOT_COUNT = 5;

export interface SaveSlot {
  /** null when the slot is empty. */
  data: SaveData | null;
  /** Player-given name, e.g. "Saturday game". Falls back to "Slot N". */
  name: string;
  /** ms epoch of last save into this slot. */
  updatedAt: number;
}

type Slots = SaveSlot[];

function readSlots(): Slots {
  try {
    const raw = localStorage.getItem(SLOTS_KEY);
    if (raw) return JSON.parse(raw) as Slots;
  } catch {
    // fall through to legacy migration / fresh slots
  }
  // Migrate legacy single-slot save into slot 0 if present.
  let migrated: SaveData | null = null;
  try {
    const old = localStorage.getItem(LEGACY_KEY);
    if (old) migrated = JSON.parse(old) as SaveData;
  } catch {
    // ignore
  }
  const slots: Slots = [];
  for (let i = 0; i < SLOT_COUNT; i++) {
    slots.push({
      data: i === 0 ? migrated : null,
      name: `Slot ${i + 1}`,
      updatedAt: i === 0 && migrated ? Date.now() : 0,
    });
  }
  writeSlots(slots);
  return slots;
}

function writeSlots(slots: Slots): void {
  try {
    localStorage.setItem(SLOTS_KEY, JSON.stringify(slots));
  } catch {
    // ignore
  }
}

export const SaveStore = {
  /** All 5 slots (always returns SLOT_COUNT entries). */
  listSlots(): Slots {
    return readSlots();
  },

  getActiveSlot(): number {
    try {
      const raw = localStorage.getItem(ACTIVE_KEY);
      if (raw === null) return 0;
      const idx = parseInt(raw, 10);
      if (Number.isFinite(idx) && idx >= 0 && idx < SLOT_COUNT) return idx;
    } catch {
      // ignore
    }
    return 0;
  },

  setActiveSlot(idx: number): void {
    try {
      localStorage.setItem(ACTIVE_KEY, String(idx));
    } catch {
      // ignore
    }
  },

  /** Save the current run into the active slot. */
  save(data: SaveData): void {
    const slots = readSlots();
    const idx = this.getActiveSlot();
    if (idx < 0 || idx >= SLOT_COUNT) return;
    slots[idx].data = data;
    slots[idx].updatedAt = Date.now();
    writeSlots(slots);
  },

  /** Save into a specific slot (and make it the active slot). */
  saveTo(idx: number, data: SaveData): void {
    if (idx < 0 || idx >= SLOT_COUNT) return;
    const slots = readSlots();
    slots[idx].data = data;
    slots[idx].updatedAt = Date.now();
    writeSlots(slots);
    this.setActiveSlot(idx);
  },

  /** Load whatever's in the active slot. */
  load(): SaveData | null {
    const slots = readSlots();
    const idx = this.getActiveSlot();
    return slots[idx]?.data ?? null;
  },

  /** Clear a single slot (without changing the active slot). */
  clearSlot(idx: number): void {
    if (idx < 0 || idx >= SLOT_COUNT) return;
    const slots = readSlots();
    slots[idx].data = null;
    slots[idx].updatedAt = 0;
    writeSlots(slots);
  },

  /** Clear the active slot. (Used by the old "New Game" path.) */
  clear(): void {
    this.clearSlot(this.getActiveSlot());
  },

  renameSlot(idx: number, name: string): void {
    if (idx < 0 || idx >= SLOT_COUNT) return;
    const slots = readSlots();
    slots[idx].name = name.trim() || `Slot ${idx + 1}`;
    writeSlots(slots);
  },
};
